import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styles from './DataSyncFormSection.module.sass';
import { DropDown, Label, SelectItem } from '@zaiusinc/hera';
import { Tooltip } from '@blueprintjs/core';
import { Destination, DestinationObject, ProductInstance, Source } from '../../../../types/DataSyncItem';
import { useObserver } from 'mobx-react-lite';
import Notification from '../Notification';
import { ValidateHandle } from './ValidateHandle';
import ProductStoreFactory from '../../../../stores/product/ProductStoreFactory';
import ContentGraphConfigSection from './ContentGraphConfigSection';
import { DataSyncObjectsStore } from '../../../../stores/object/DataSyncObjectsStore';
import { OptimizelyProduct } from '../../../../types/DataSyncItem';
import DataSyncCgObjectsStore from '../../../../stores/object/DataSyncCgObjectsStore';
import { CGObjectCreateDialog } from './CGObjectCreateDialog';
import { showToasts } from '../../../../lib/toaster';
import _ from 'lodash';
import { IconSize, ObjectIcon } from '../ObjectIcon';
import { getNotificationLabelForObject, getObjectLabel } from '../../../../lib/dataSync';
import { useDestinationAvailability } from '../../../../hooks/useAvailabilityCheck';

interface DataSyncFormDestinationProps {
  disabled?: boolean;
  onChange: (value: Destination) => void;
  initialValue: Destination;
  dataSyncObjectsStore: DataSyncObjectsStore | null;
  inheritSourceSchema: (destination: Destination, baseType?: string) => Promise<void>;
  source: Source;
}

const DataSyncFormDestination = forwardRef<ValidateHandle, DataSyncFormDestinationProps>(
  (props, ref) => {
    const {disabled, initialValue, onChange, dataSyncObjectsStore, inheritSourceSchema, source} = props;
    const [destination, setValue] = React.useState<Destination>({...initialValue});
    const [errors, setErrors] = React.useState<{ [key: string]: boolean }>({
      product: false,
      instance: false,
      object: false,
    });
    const [objectLabel, setObjectLabel] = useState<string>(getObjectLabel(destination.product));
    const contentGraphConfigRef = useRef<ValidateHandle>(null);
    const [showCGObjectCreateDialog, setShowCGObjectCreateDialog] = React.useState<boolean >(false);

    // Use custom hook for availability checking
    const { instanceUnavailable, objectUnavailable } = useDestinationAvailability(destination, dataSyncObjectsStore);

    useImperativeHandle(ref, () => ({
      validate: () => {
        setErrors({
          product: !destination.product,
          instance: !destination.instance.id,
          object: !destination.object.object_name,
        });

        let contentGraphConfigValid = true;
        if (destination.product === OptimizelyProduct.CGR) {
          contentGraphConfigValid = contentGraphConfigRef.current?.validate() ?? false;
        }

        return !!(!instanceUnavailable && !objectUnavailable && destination.product
          && destination.instance.id && destination.object.object_name && contentGraphConfigValid);
      },
    }));

    const productList = (): SelectItem[] => {
      return ProductStoreFactory.getDestinationProductStore().getProductList().map((product) => {
        return {
          text: product.text,
          value: product.value,
          icon: product.iconPath ?
            (<ObjectIcon size={IconSize.SMALL} src={product.iconPath} alt={product.text}/>) : undefined,
        };
      }) || [];
    };

    const instanceList = (productName: OptimizelyProduct): SelectItem[] => {
      const destinationProductStore = ProductStoreFactory.getDestinationProductStore();
      const items = destinationProductStore.getInstanceList(
        productName,
        destination.instance?.id,
        destination.instance?.name
      );

      return items.map((product) => {
        let icon: React.ReactElement = <></>;

        if (product.iconPath) {
          icon = (<ObjectIcon size={IconSize.SMALL} src={product.iconPath} alt={product.text}/>);
        } else if (productName === OptimizelyProduct.HUB_APP) {
          icon = (<ObjectIcon size={IconSize.SMALL} src={'/assets/data_platform.svg'} alt={product.text}/>);
        }
        const isHubApp = productName === OptimizelyProduct.HUB_APP;
        return {
          text: product.text,
          value: product.value,
          icon,
          disabled: product.unavailable,
          disabledTooltip: product.unavailable
            ? isHubApp
              ? 'This app is no longer available. It may not be installed in this account anymore. Please select a different app.'
              : 'This instance is no longer available. Please select a different instance.'
            : undefined,
        };
      }) || [];
    };

    const objectList = () => {
      const items = dataSyncObjectsStore?.getDestinationObjectList(
        destination.object?.object_name,
        destination.object?.object_name
      );

      const objects = items?.map((object) => {
        return {
          text: object.object_display_name || object.object_name,
          value: object.object_name,
          disabled: object.disabled,
          disabledTooltip: object.warning,
        };
      }) || [];

      const transientObjectDefinition = dataSyncObjectsStore?.getTransientDestinationObject();
      if (transientObjectDefinition && !objects.find((obj) => obj.value === transientObjectDefinition.object_name)) {
        objects.push({
          text: transientObjectDefinition.object_name,
          value: transientObjectDefinition.object_name,
          disabled: false,
          disabledTooltip: undefined,
        });
      }

      return objects;
    };

    const getInstanceLabel = (): string => {
      if (destination.product === OptimizelyProduct.HUB_APP) {
        return 'App';
      } else {
        return 'Instance';
      }
    };

    const getInstanceLoading = (): boolean => {
      if (!destination?.product) {
        return false;
      }
      const destinationProductStore = ProductStoreFactory.getDestinationProductStore();
      return destination.product === OptimizelyProduct.HUB_APP
        ? destinationProductStore.hubAppsLoading
        : destinationProductStore.productInstancesLoading;
    };

    const onClickInheritSourceObject = async (destinationObjectName: string, baseType?: string) => {
      const newDestination = {
        ...destination,
        object: {
          ...destination.object,
          object_name: destinationObjectName
        }
      };
      await inheritSourceSchema(newDestination, baseType);
      setValue(newDestination);
      setShowCGObjectCreateDialog(false);
      showToasts([
        {
          intent: 'success',
          message:
            'Destination content type will be persisted after saving the data sync.',
        },
      ]);
    };

    const renderInheritSourceObjectOption = () => {
      if (destination.product === OptimizelyProduct.CGR) {
        const isEnabled = source?.object?.object_name && destination?.instance?.id;
        return (
          <Tooltip
            content="Map each source field to a graph field."
            placement="bottom"
            key="tooltip-inherit-source-object"
            fill
          >
            <div
              key="inherit-source-object"
              className={`bp5-menu-item ${
                !isEnabled ? 'bp5-disabled' : styles.inheritSourceText
              }`}
              onClick={() => setShowCGObjectCreateDialog(true)}
            >
              + Clone Source schema
            </div>
          </Tooltip>
        );
      } else {
        return undefined;
      }
    };

    return useObserver(() => (
      <div className={styles.container}>
        <Label
          label="Destination"
          disabled={disabled}
          subLabel="Select an Optimizely product as destination to sync data."
        >
          <DropDown
            disabled={disabled}
            initialContent="Select Product"
            items={productList()}
            onItemSelect={(item: any) => {
              const newData = { ...destination, product: item.value };
              if (destination.product !== item.value) {
                newData.instance = {} as ProductInstance;
                newData.object = {} as DestinationObject;
                newData.config = {};
                setObjectLabel(getObjectLabel(item.value));
              }
              setValue(newData);
              onChange(newData);
              setErrors({ ...errors, product: false });
            }}
            value={destination.product}
            width={390}
          />
          <Notification
            message="Please select a product"
            show={errors.product}
            type="error"
          />
        </Label>

        <Label label={getInstanceLabel()} disabled={disabled}>
          <DropDown
            disabled={disabled}
            initialContent={'Select ' + getInstanceLabel()}
            items={instanceList(destination.product) || []}
            loading={getInstanceLoading()}
            className={instanceUnavailable ? styles.unavailableSelected : ''}
            onItemSelect={(item: any) => {
              const newData = {
                ...destination,
                instance: { id: item.value, name: item.text },
              };
              if (destination.instance.id !== item.value) {
                newData.object = {} as DestinationObject;
              }
              setValue(newData);
              onChange(newData);
              setErrors({ ...errors, instance: false });
            }}
            value={destination.instance.id}
            width={390}
          />
          <Notification
            message={'Please select an ' + getInstanceLabel().toLowerCase()}
            show={errors.instance}
            type="error"
          />
          <Notification
            message={
              destination.product === OptimizelyProduct.HUB_APP
                ? 'The app this sync is configured for is no longer available. It may not be installed in this account anymore. Please select a different app.'
                : 'The instance this sync is configured for is no longer available. Either you don\'t have access to the instance or it does not exist anymore. Please select a different instance.'
            }
            show={instanceUnavailable}
            type="warning"
          />
        </Label>

        <Label label={objectLabel} disabled={disabled}>
          <DropDown
            filterable
            className={`objectDropDown ${objectUnavailable ? styles.unavailableSelected : ''}`}
            disabled={disabled || instanceUnavailable}
            initialContent={`Select ${objectLabel}`}
            items={objectList()}
            loading={dataSyncObjectsStore?.loading || false}
            onItemSelect={function noRefCheck(item: any) {
              const newData = {
                ...destination,
                object: {
                  ...destination.object,
                  object_name: item.value,
                },
              };
              setValue(newData);
              onChange(newData);
              setErrors({ ...errors, object: false });
            }}
            value={destination.object.object_name}
            width={390}
            bottomSection={renderInheritSourceObjectOption()}
          />
          <Notification
            message={`Please select ${getNotificationLabelForObject(destination.product)}`}
            show={errors.object}
            type="error"
          />
          <Notification
            message={
              destination.product === OptimizelyProduct.CGR
                ? 'The content type this sync is configured for is no longer available. Either you don\'t have access to the content type or it does not exist anymore. Please select a different content type.'
                : 'The object this sync is configured for is no longer available. Either you don\'t have access to the object or it does not exist anymore. Please select a different object.'
            }
            show={objectUnavailable}
            type="warning"
          />
        </Label>

        {destination.product === OptimizelyProduct.CGR && (
          <ContentGraphConfigSection
            instanceId={destination.instance?.id}
            initialValue={destination.config?.content_graph}
            ref={contentGraphConfigRef}
            onChange={(value) => {
              const newData = { ...destination };
              newData.config = { content_graph: value };
              setValue(newData);
              onChange(newData);
            }}
            dataSyncCgObjectStore={
              dataSyncObjectsStore as DataSyncCgObjectsStore
            }
            disabled={disabled || instanceUnavailable || objectUnavailable || !dataSyncObjectsStore}
          />
        )}

        {showCGObjectCreateDialog && (
          <CGObjectCreateDialog
            initialObjectName={_.upperFirst(_.camelCase(source.object.object_name))}
            isOpen={showCGObjectCreateDialog}
            onSave={onClickInheritSourceObject}
            onClose={() => setShowCGObjectCreateDialog(false)}
            dataSyncCGObjectsStore={dataSyncObjectsStore as DataSyncCgObjectsStore}
          />
        )}
      </div>
    ));
  });

export default DataSyncFormDestination;
